import { MouseEvent, useMemo, useState } from 'react'

import IconButton from '@material-ui/core/IconButton'
import Menu from '@material-ui/core/Menu'
import MenuItem from '@material-ui/core/MenuItem'
import Typography from '@material-ui/core/Typography'
import { makeStyles } from '@material-ui/core/styles'
import GetAppIcon from '@material-ui/icons/GetApp'
import MoreHorizIcon from '@material-ui/icons/MoreHoriz'
import StarIcon from '@material-ui/icons/Star'

import { MAX_RUNTIME_CONFIG } from '../../config/max-env.config'

type MenuEntry = {
  id: string
  label: string
  type: 'item' | 'header'
  icon?: JSX.Element
}

type ChatHeaderProps = {
  title: string
  isConnected?: boolean
  statusText?: string
  showConversationOptions?: boolean
  onOptionSelect?: (id: string) => void
}

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3, 4),
    borderBottom: '1px solid var(--ds-border-default, #d7dae4)',
    background: 'var(--ds-bg-elevated, #fff)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
  },
  title: {
    fontSize: '1.125rem',
    lineHeight: 1.3,
    fontWeight: 600,
    color: 'var(--ds-fg-primary, #1f2430)',
    [theme.breakpoints.up('md')]: {
      fontSize: '1.25rem',
    },
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  statusGroup: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  statusDot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #34d399 0%, #10b981 100%)',
    animation: '$pulse 1.6s ease-in-out infinite',
  },
  statusDotDisconnected: {
    background: '#ef4444',
    animation: 'none',
  },
  statusText: {
    color: 'var(--ds-fg-secondary, #535b6c)',
    fontSize: '0.75rem',
    [theme.breakpoints.up('md')]: {
      fontSize: '0.875rem',
    },
  },
  optionsButton: {
    width: 32,
    height: 32,
    borderRadius: 8,
    color: 'var(--ds-fg-secondary, #535b6c)',
    '&:hover': {
      background: 'var(--ds-bg-subtle, #f1f2f7)',
      color: 'var(--ds-fg-primary, #1f2430)',
    },
  },
  menuPaper: {
    minWidth: 224,
    borderRadius: 16,
    border: '1px solid var(--ds-border-default, #d7dae4)',
    background: 'var(--ds-bg-elevated, #fff)',
    boxShadow: '0 18px 36px rgba(15, 23, 42, 0.16)',
    padding: theme.spacing(0.5),
    marginTop: theme.spacing(1),
  },
  menuItem: {
    borderRadius: 10,
    padding: theme.spacing(1, 1.25),
    fontSize: '0.875rem',
    color: 'var(--ds-fg-primary, #1f2430)',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    '&:hover': {
      background: 'var(--ds-bg-subtle, #f1f2f7)',
    },
  },
  menuHeader: {
    pointerEvents: 'none',
    opacity: 1,
    borderRadius: 10,
    padding: theme.spacing(1, 1.25, 0.75),
    fontSize: '0.75rem',
    fontWeight: 600,
    color: 'var(--ds-fg-secondary, #535b6c)',
    textTransform: 'none',
    '&:hover': {
      background: 'transparent',
    },
  },
  menuIcon: {
    color: 'inherit',
    fontSize: 16,
  },
  '@keyframes pulse': {
    '0%': {
      transform: 'scale(1)',
      opacity: 0.85,
    },
    '50%': {
      transform: 'scale(1.15)',
      opacity: 1,
    },
    '100%': {
      transform: 'scale(1)',
      opacity: 0.85,
    },
  },
}))

const menuItems: MenuEntry[] = [
  {
    id: 'show-reports',
    label: 'Show reports',
    type: 'item',
    icon: <GetAppIcon fontSize="small" />,
  },
  {
    id: 'header-export-conversation',
    label: 'Export Conversation',
    type: 'header',
    icon: <GetAppIcon fontSize="small" />,
  },
  {
    id: 'export-conversation-pdf',
    label: 'To PDF',
    type: 'item',
    icon: <GetAppIcon fontSize="small" />,
  },
  {
    id: 'export-conversation-docx',
    label: 'To Docx',
    type: 'item',
    icon: <GetAppIcon fontSize="small" />,
  },
  {
    id: 'export-conversation-ppt',
    label: 'To PPT',
    type: 'item',
    icon: <GetAppIcon fontSize="small" />,
  },
  {
    id: 'header-export-favorites',
    label: 'Export favorites',
    type: 'header',
    icon: <StarIcon fontSize="small" />,
  },
  {
    id: 'export-favorites-pdf',
    label: 'To PDF',
    type: 'item',
    icon: <GetAppIcon fontSize="small" />,
  },
  {
    id: 'export-favorites-docx',
    label: 'To Docx',
    type: 'item',
    icon: <GetAppIcon fontSize="small" />,
  },
]

const ChatHeader = ({
  title,
  isConnected = true,
  statusText = 'Connected',
  showConversationOptions = false,
  onOptionSelect,
}: ChatHeaderProps) => {
  const classes = useStyles()
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)

  const isDevelopment = useMemo(() => MAX_RUNTIME_CONFIG.environment === 'dev', [])

  const handleMenuOpen = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
  }

  const handleSelect = (id: string) => {
    onOptionSelect?.(id)
    handleMenuClose()
  }

  return (
    <header className={classes.root}>
      <Typography className={classes.title}>{title}</Typography>

      <div className={classes.actions}>
        {isDevelopment ? (
          <div className={classes.statusGroup}>
            <span
              className={`${classes.statusDot} ${!isConnected ? classes.statusDotDisconnected : ''}`}
            />
            <Typography className={classes.statusText}>{statusText}</Typography>
          </div>
        ) : null}

        {showConversationOptions ? (
          <>
            <IconButton
              aria-haspopup="menu"
              aria-expanded={Boolean(anchorEl)}
              aria-label="Open conversation options"
              className={classes.optionsButton}
              onClick={handleMenuOpen}
            >
              <MoreHorizIcon fontSize="small" />
            </IconButton>

            <Menu
              anchorEl={anchorEl}
              keepMounted
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
              getContentAnchorEl={null}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              PaperProps={{ className: classes.menuPaper }}
            >
              {menuItems.map((item) => (
                <MenuItem
                  key={item.id}
                  disabled={item.type === 'header'}
                  className={item.type === 'header' ? classes.menuHeader : classes.menuItem}
                  onClick={() => handleSelect(item.id)}
                >
                  {item.icon ? <span className={classes.menuIcon}>{item.icon}</span> : null}
                  {item.label}
                </MenuItem>
              ))}
            </Menu>
          </>
        ) : null}
      </div>
    </header>
  )
}

export default ChatHeader
